import { SiteTemplate, SiteType, type StateResponse } from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import {
  HighSchoolHome,
  type IHighSchoolHomeProps,
} from 'components/Home/Templates/HighSchoolHome';
import { RivalsHome } from 'components/Home/Templates/RivalsHome';
import {
  type ITraditionalHomeProps,
  TraditionalHome,
} from 'components/Home/Templates/TraditionalHome';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { GraphSchema } from 'store/schemas/Graph';
import { OrganizationSchema } from 'store/schemas/Organization';
import { ScheduleSchema } from 'store/schemas/Schedule';
import { createCanonicalUrl, createDescription, createTitle } from 'utils/meta';
import { getSiteDataByTemplateType, getSiteState } from 'utils/sites';

interface ISiteResponse
  extends IGenericPageProps,
    ITraditionalHomeProps,
    IHighSchoolHomeProps {
  state: StateResponse | null;
}

export const TeamPage = ({ siteData, state, ...props }: ISiteResponse) => {
  const { currentSite } = siteData || {};
  const { template, type } = currentSite || {};
  const { organization, siteName, url: siteUrl } = currentSite || {};
  const orgName = organization?.knownAs ?? organization?.name;

  let title = createTitle(
    siteName && `${siteName}:`,
    orgName,
    organization?.mascot,
    'Football & Basketball',
  );

  let description = createDescription(
    `The latest {0} {1} news, recruiting, and NIL information at {2}, part of on3.com`,
    orgName,
    organization?.mascot,
    siteName,
  );

  if (type === SiteType.HighSchool) {
    title = createTitle(
      state?.name,
      'High School Sports News, Rankings, Scores & Recruiting | On3',
    );

    description = createDescription(
      'Stay updated with {0} high school sports — football, basketball, baseball, and more. Get the latest news, scores, schedules, and recruiting updates from On3.',
      state?.name,
    );
  }

  // Josh Pate show
  if (currentSite.key === 488) {
    title = "Josh Pate's College Football Show";
    description =
      "Josh Pate delivers America's College Football Show the way you asked for it. No debates or hot takes - just a channel for those of us who live for Saturdays in the fall. We talk College Football all year - from game predictions and reactions to sit-downs with the biggest names in the sport to best bets and the world-famous JP Poll.";
  }

  const canonical = createCanonicalUrl(siteUrl as string);

  const schemaItems = [
    new OrganizationSchema().toJSON(),
    new OrganizationSchema({
      name: orgName || '',
      url: canonical,
      logo: {
        url: getAssetUrl(organization?.defaultAssetRes) || '',
        width: organization?.defaultAssetRes?.width || 0,
        height: organization?.defaultAssetRes?.height || 0,
      },
    }).toJSON(),
  ];

  if (props.schedule) {
    schemaItems.push(
      new ScheduleSchema({
        name: organization?.name || '',
        url: canonical,
        sport: props.schedule.relatedModel?.sport?.name || '',
        event: props.schedule.list || [],
      }).toJSON(),
    );
  }

  const schema = new GraphSchema({
    items: schemaItems,
  }).toJSON();

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      schema={schema}
      siteData={siteData}
      title={title}
    >
      {template === SiteTemplate.Traditional && <TraditionalHome {...props} />}
      {template === SiteTemplate.HighSchool && <HighSchoolHome {...props} />}
      {template === SiteTemplate.Rivals && <RivalsHome {...props} />}
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { currentSite, defaultYears } = siteData;

  if (!currentSite) {
    return {
      notFound: true,
    };
  }

  const state = getSiteState(currentSite.key) ?? null;

  try {
    const data = {
      siteData,
      userData: userData || {},
    };

    const promises = getSiteDataByTemplateType(
      currentSite.template,
      currentSite,
      {
        year: defaultYears?.footballRecruitingYear,
        stateAbbr: state?.abbreviation,
        stateKey: state?.key,
        sportKey: 1, // Default to football
      },
    );

    // Fetch all site data in parallel here
    const resolvedPromises = await Promise.allSettled(
      Object.values(promises).map((promise) =>
        promise ? promise() : Promise.resolve(null),
      ),
    );

    resolvedPromises.forEach((promise, i) => {
      const key = Object.keys(promises)[i];

      Object.assign(data, { [key]: getFulfilledValue(promise) });
    });

    return { props: { ...data, state, pageType: 'home' } };
  } catch (e) {
    // console.log(e);
    return { props: {} };
  }
};

export default withValidUrl(TeamPage);
